import { extractLinks } from "@draft-js-plugins/linkify";
import { ContentState } from "draft-js";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useRef, useState } from "react";
import { useServices } from "@core/servicesContext";
import {
	AsyncResult,
	PaginatedDataResult,
	useAsync,
	useChangeEffect,
	usePaginatedStoreWithArgs,
	useStoredData,
} from "@utils/hooks";
import { ImageVersions } from "./dtos";
import { AssetType, Media } from "./media";
import { OpenGraphData } from "./openGraphData";

const ASSET_POLLING_INTERVAL = 10000;
export function useAssetPolling(id: string): AsyncResult<Media> {
	const { mediaService } = useServices();
	const { result, loading, error } = useStoredData(id, mediaService.assetStore, []);
	const fetchTimeout = useRef<NodeJS.Timeout>(null);

	useEffect(() => {
		const run = async (): Promise<void> => {
			if (result?.metadata?.status === "IN_PROGRESS" || (!!result && !result.metadata?.status)) {
				fetchTimeout.current = setTimeout(async () => {
					await mediaService.assetStore.fetch(id);
					await run();
				}, ASSET_POLLING_INTERVAL);
			}
		};
		void run();
		return () => {
			if (fetchTimeout.current) clearTimeout(fetchTimeout.current);
		};
	}, [result?.metadata?.status, !!result]);

	return { result, loading, error };
}

export function useOpenGraphData(
	contentState: ContentState,
	initialData?: OpenGraphData
): { data: OpenGraphData | null; loading: boolean; setData: (data: OpenGraphData | null) => void } {
	const { mediaService } = useServices();
	const [data, setData] = useState<OpenGraphData | null>(initialData ?? null);
	const { loading, run } = useAsync(async (url: string) => {
		try {
			const fetchedData = await mediaService.fetchOpenGraphData(url);
			setData(fetchedData);
		} catch {
			setData(null);
		}
	}, []);

	const updateData = useCallback(debounce(run, 1000), []);

	const links = extractLinks(contentState.getPlainText());
	const lastLink: string | undefined = links?.[links.length - 1]?.url;

	useChangeEffect(() => {
		if (lastLink) {
			updateData(lastLink);
		}
	}, [lastLink]);

	return { data, loading, setData };
}

export function useSourceLoaded(src?: string): string | undefined {
	const [sourceLoaded, setSourceLoaded] = useState<string | undefined>();

	useEffect(() => {
		if (src) {
			const bgImg = new Image();
			bgImg.src = src;
			bgImg.onload = () => setSourceLoaded(src);
		}
	}, [src]);

	return sourceLoaded;
}

export function useSource(src?: string): { sourceLoaded: string | undefined; loading: boolean } {
	const [sourceLoaded, setSourceLoaded] = useState<string | undefined>();
	const [loading, setLoading] = useState<boolean>(true);

	useEffect(() => {
		if (src) {
			const bgImg = new Image();
			bgImg.onload = () => {
				setSourceLoaded(src);
				setLoading(false);
			};
			bgImg.src = src;
		}

		if (!src) {
			setLoading(false);
		}
	}, [src]);

	return { sourceLoaded, loading };
}

export function useImage(
	image: Media,
	size: ImageVersions,
	zoom?: boolean
): { loaded: boolean; error: boolean; imageUrl: string; zoomUrl: string; zoomLoaded: boolean } {
	const [loaded, setLoaded] = useState(false);
	const [error, setError] = useState(false);
	const [zoomLoaded, setZoomLoaded] = useState(false);
	const imageUrl = !["image/gif", "image/webp"].includes(image.mimeType)
		? (image.versions?.[size] ?? image.url)
		: image.url;
	const zoomUrl = !["image/gif", "image/webp"].includes(image.mimeType)
		? (image.versions?.[ImageVersions.LARGE] ?? imageUrl)
		: imageUrl;

	useEffect(() => {
		const img = new Image();
		img.onload = () => setLoaded(true);
		img.onerror = () => setError(true);
		img.src = imageUrl;
	}, [imageUrl]);

	useEffect(() => {
		if (zoom && !zoomLoaded) {
			const img = new Image();
			img.onload = () => setZoomLoaded(true);
			img.src = zoomUrl;
		}
	}, [zoom, zoomUrl]);

	return { loaded, error, imageUrl, zoomUrl, zoomLoaded };
}

export function useMyAssets(query: string, type?: AssetType): PaginatedDataResult<Media> {
	const { mediaService } = useServices();

	return usePaginatedStoreWithArgs(mediaService.myAssetsStore, [], query, type);
}
